import { Component, h } from 'preact'
import axios from 'axios'
import Header from '../header'
import { Bar } from 'react-chartjs-2'
import { ChartOptions } from 'chart.js'
import { DashboardTimezoneDisclaimer } from './DashboardTimezoneDisclaimer'
import { FilterOptionDate, StoredState } from './interfaces'
import AnalyticsDateFormatter from './AnalyticsDateFormatter'
import DashboardDateRangeFilter from './DashboardDateRangeFilter'
import {
  getBrandBlue,
  getBrandBorderGrey,
  getBrandGreen,
  getDefaultDateRange,
  getDefaultSelectedDate,
  trackDashboardUsage
} from './utils'
import { Loader } from './Loader'

interface Data {
  count: number,
  time: number
}

interface Props {}

interface State {
  pageViewData: Array<Data>,
  uniqueVisitorsData: Array<Data>,
  activeCustomersData: Array<Data>,
  customers: Array<Data>,
  selectedPresetDateRangeId: string,
  startDate: Date,
  endDate: Date,
  selectedDate: FilterOptionDate,
  presetDateRangeList: Array<FilterOptionDate>,
  isDateRangeOpen: boolean,
  storedState: StoredState,
  isLoading: boolean,
  isPageViewsLoading: boolean,
  isUniqueVisitorsLoading: boolean,
  isTotalVsActiveCustomersLoading: boolean,
  isPercentageMonthlyActiveCustomersLoading: boolean
}

class EngagementGraphs extends Component<Props, State> {
    private brandBlue = getBrandBlue()
    private brandGreen = getBrandGreen()
    private borderGrey = getBrandBorderGrey()
    wrapperRef: null | HTMLElement = null
  constructor () {
      super()
      let monthStartDate = AnalyticsDateFormatter.getMonthStartDate()
      let yesterday = AnalyticsDateFormatter.getYesterday()
      this.setState({
        pageViewData: [],
        uniqueVisitorsData: [],
        activeCustomersData: [],
        customers: [],
        selectedPresetDateRangeId: 'last_thirty_days',
        storedState: {
          presetDateRangeId: 'last_thirty_days',
          selectedDateRange: getDefaultSelectedDate()
        },
        startDate: monthStartDate,
        endDate: yesterday,
        presetDateRangeList: [],
        isDateRangeOpen: false,
        isLoading: false
      })
      this.brandBlue = getBrandBlue()
      this.brandGreen = getBrandGreen()
      this.borderGrey = getBrandBorderGrey()

      this.toggleDateRange = this.toggleDateRange.bind(this)
      this.onDateRangeChanged = this.onDateRangeChanged.bind(this)
      this.onResetDateRange = this.onResetDateRange.bind(this)
      this.onPresetDateRangeChanged = this.onPresetDateRangeChanged.bind(this)
      this.onCustomDateRangeChanged = this.onCustomDateRangeChanged.bind(this)
    }

    async componentDidMount () {
      trackDashboardUsage('engagement')
      this.setState({
        isLoading: true,
        isPageViewsLoading: true,
        isUniqueVisitorsLoading: true,
        isTotalVsActiveCustomersLoading: true,
        isPercentageMonthlyActiveCustomersLoading: true
      })
      try {
        const presetDateRanges = await this.fetchDateRangeFilter()
        const defaultDateRange = getDefaultDateRange(presetDateRanges, this.state.selectedPresetDateRangeId)
        const updatedState = this.state.storedState
        updatedState.presetDateRangeId = defaultDateRange.id
        updatedState.selectedDateRange = defaultDateRange
        this.setState({
          presetDateRangeList: presetDateRanges,
          selectedDate: defaultDateRange,
          startDate: new Date(defaultDateRange.startDate),
          endDate: new Date(defaultDateRange.endDate),
          storedState: updatedState,
          isLoading: false
        })
      } catch {
        this.setState({ isLoading: false })
      }
      this.fetchPageViewData()
      this.fetchUniqueVisitorsData()
      this.fetchActiveCustomersData()
      this.fetchTotalCustomers()
    }

  addHandler () {
    const { isDateRangeOpen } = this.state
    if(isDateRangeOpen ) {
      window.document.addEventListener('mousedown', this.handleClickOutside)
    }
  }

  removeHandler () {
    window.document.removeEventListener('mousedown', this.handleClickOutside)
  }

  handleClickOutside = (e: MouseEvent) => {
    if (!this.wrapperRef) return
    if (!this.wrapperRef.contains(e.target as HTMLElement)) {
      this.onResetDateRange()
      this.setState({}, this.removeHandler)
    }
  }

    async fetchDateRangeFilter () {
      const res = await axios.get('/analyticsV2/fetchDateRangeFilter')
      return res.data.ranges
    }

    async fetchUniqueVisitorsData () {
      this.setState({ isUniqueVisitorsLoading: true })
      try {
        const response = await this.fetchViewData('unique_visitors')
        this.setState({
          uniqueVisitorsData: response,
          isUniqueVisitorsLoading: false
        })
      } catch {
        this.setState({ isUniqueVisitorsLoading: false })
      }
    }

    async fetchPageViewData () {
      this.setState({ isPageViewsLoading: true })
      try {
        const response = await this.fetchViewData('page_views')
        this.setState({
          pageViewData: response,
          isPageViewsLoading: false
        })
      } catch {
        this.setState({ isPageViewsLoading: false })
      }
    }

    async fetchActiveCustomersData () {
      this.setState({ isTotalVsActiveCustomersLoading: true })
      try {
        const response = await this.fetchViewData('active_customers')
        this.setState({
          activeCustomersData: response,
          isTotalVsActiveCustomersLoading: false
        })
      } catch {
        this.setState({ isTotalVsActiveCustomersLoading: false })
      }
    }

   async fetchTotalCustomers () {
      this.setState({ isPercentageMonthlyActiveCustomersLoading : true })
      try {
        const {startDate, endDate} = this.state
        const filterOptions = {
          start: AnalyticsDateFormatter.formatDate(startDate),
          end: AnalyticsDateFormatter.formatDate(endDate, false)
        }
        const res = await axios.get(`/company/customers?start=${filterOptions.start}&end=${filterOptions.end}`)
        this.setState({
          customers: res.data,
          isPercentageMonthlyActiveCustomersLoading : false
        })
      } catch {
        this.setState({ isPercentageMonthlyActiveCustomersLoading : false })
      }
    }

    async fetchViewData (viewName: string) {
      const { startDate, endDate } = this.state
      const filterOptions = {
        start: AnalyticsDateFormatter.formatDate(startDate),
        end: AnalyticsDateFormatter.formatDate(endDate, false)
      }
      const res = await axios.post('/analyticsV2/fetchViewData',
        {
          view: viewName,
          filters: filterOptions
        })
      return res.data
    }

    async refetchData () {
      this.setState({ isLoading: true })

      try {
        this.fetchPageViewData()
        this.fetchUniqueVisitorsData()
        this.fetchActiveCustomersData()
        this.fetchTotalCustomers()
        this.setState({ isLoading: false })

      } catch {
        this.setState({ isLoading: false })
      }
    }

    toggleDateRange (value: boolean) {
      this.setState({ isDateRangeOpen: value }, this.addHandler)
    }

    onPresetDateRangeChanged (selectedDate: FilterOptionDate) {
      this.setState({
        selectedPresetDateRangeId: selectedDate.id,
        selectedDate: selectedDate
      })
    }

    onCustomDateRangeChanged (dates: Date[]) {
      const customSelectedDate = {
        id: 'custom_range',
        label: 'Custom Range',
        startDate: dates[0],
        endDate: dates[1]
      }
      this.setState({
        selectedDate: customSelectedDate,
        selectedPresetDateRangeId: customSelectedDate.id
      })
    }

    onDateRangeChanged () {
      const { selectedDate, selectedPresetDateRangeId } = this.state
      const updatedState = this.state.storedState
      updatedState.presetDateRangeId = selectedPresetDateRangeId
      updatedState.selectedDateRange = selectedDate
      this.setState({
        isDateRangeOpen: false,
        startDate: new Date(selectedDate.startDate),
        endDate: new Date(selectedDate.endDate),
        storedState: updatedState
      })
      this.refetchData()
    }

    onResetDateRange () {
      const { presetDateRangeId, selectedDateRange } = this.state.storedState
      this.setState({
        isDateRangeOpen: false,
        selectedPresetDateRangeId: presetDateRangeId,
        selectedDate: selectedDateRange
      })
    }

    getBarChartOptions (displayLegend: boolean = true): ChartOptions {
      return {
        title: {
          display: false
        },
        layout: {
          padding: {
            left: 0,
            right: 0,
            top: 0,
            bottom: 24
          }
        },
        responsive: true,
        maintainAspectRatio: false,
        tooltips: {
          intersect: false,
          mode: 'index'
        },
        legend: {
          display: displayLegend,
          position: 'top',
          align: 'start'
        },
        scales: {
          xAxes: [{
            gridLines: {
              display: false
            }
          }],
          yAxes: [
            {
              gridLines: {
                borderDash: [8, 4],
                color: this.borderGrey,
                drawBorder: false
              },
              ticks: {
                padding: 16,
                beginAtZero: true
              }
            }
          ]
        }
      }
    }

    getBarChartDataDataSet (data: Array<Data>, label: string) {
      return {
        labels: data.map(d => d.time),
        datasets: [
          {
            label: label,
            fill: false,
            data: data.map(d => d.count),
            borderColor: this.brandBlue,
            backgroundColor: this.brandBlue
          }
        ]
      }
    }

    render (_: Props, { pageViewData, uniqueVisitorsData, activeCustomersData, customers, isLoading, isDateRangeOpen, selectedPresetDateRangeId, startDate, endDate, presetDateRangeList, selectedDate, isPageViewsLoading, isUniqueVisitorsLoading, isTotalVsActiveCustomersLoading, isPercentageMonthlyActiveCustomersLoading }: State) {
      const uniqueVisitorsLabel = 'Unique visitors'
      const pageViewsLabel = 'Page views'
      const activeCustomersPercentageLabel = '% monthly active customers'
      let activeCustomersPercentageDataSet = {}

      const activeCustomersDataSet = {
        labels: activeCustomersData.map(d => d.time),
        datasets: [
          {
            label: 'Total active customers',
            fill: false,
            data: activeCustomersData.map(d => d.count),
            borderColor: this.brandBlue,
            backgroundColor: this.brandBlue
          },
          {
            label: 'Total customers',
            fill: false,
            data: customers.map(d => d.count),
            borderColor: this.brandGreen,
            backgroundColor: this.brandGreen
          }
        ]
      }

      if (Array.isArray(customers) && customers.length) {
        activeCustomersPercentageDataSet = {
          labels: activeCustomersData.map(d => d.time),
          datasets: [
            {
              label: activeCustomersPercentageLabel,
              fill: false,
              data: activeCustomersData.map((value, index) => {
                let mac = (value.count / customers[index]['count']) * 100
                return mac.toFixed()
              }),
              borderColor: this.brandBlue,
              backgroundColor: this.brandBlue
            }
          ]
        }
      }

      return (
        <div className='engagement-dashboard-overview dashboard-overview'>
          <Header
            title='Engagement Dashboard'
            disableCancelButton
            disableSaveButton
            isBeta
          />
           <div className='filter-container' ref={node => this.wrapperRef = node}>
             <DashboardDateRangeFilter isLoading={isLoading}
              startDate={startDate}
              endDate={endDate}
              isDropdownOpen={isDateRangeOpen}
              items={presetDateRangeList}
              selectedDate={selectedDate}
              selectedPresetId={selectedPresetDateRangeId}
              toggleDropdown={this.toggleDateRange}
              onChange={this.onDateRangeChanged}
              onReset={this.onResetDateRange}
              onCustomDateChange={this.onCustomDateRangeChanged}
              onPresetDateChange={this.onPresetDateRangeChanged} />
          </div>
          <div className='graph-wrapper'>
            <div className='graph-container'>
               <div className='graph-row'>
                <div className='graph-title'> Page Views </div>
                 {isPageViewsLoading ? <Loader />
                 : <Bar data={this.getBarChartDataDataSet(pageViewData, pageViewsLabel)} options={this.getBarChartOptions(false)} />}
              </div>
              <div className='graph-row'>
                <div className='graph-title'> Unique Visitors </div>
                {isUniqueVisitorsLoading ? <Loader />
                : <Bar data={this.getBarChartDataDataSet(uniqueVisitorsData, uniqueVisitorsLabel)} options={this.getBarChartOptions(false)} />}
              </div>
              {Array.isArray(customers) && customers.length ? <div className='graph-row'>
                <div className='graph-title'> Total active customers / total customers </div>
                    {isTotalVsActiveCustomersLoading ? <Loader /> : <Bar data={activeCustomersDataSet} options={this.getBarChartOptions(true)} />}
              </div> : null}

              {Array.isArray(customers) && customers.length ? <div className='graph-row'>
                <div className='graph-title'> % monthly active customers </div>
                    {isPercentageMonthlyActiveCustomersLoading ? <Loader /> : <Bar data={activeCustomersPercentageDataSet} options={this.getBarChartOptions(false)} />}
              </div> : null}
            </div>
          </div>
          <DashboardTimezoneDisclaimer />
        </div>)
    }
}
export default EngagementGraphs
